import React from "react";
import { Table, Tag } from "antd";
import moment from "moment";

function SalePendingDataTable({ data, totals, meta, onPageChange, loading }) {
  const columns = [
    {
      title: "User",
      dataIndex: "user_name",
      key: "user_name",
      width: 200,
      fixed: "left",
      render: (name, record) => {
        if (name && record.user_account_no) {
          return `${name} (${record.user_account_no})`;
        } else if (name) {
          return name;
        } else if (record.userid) {
          return `User ID: ${record.userid}`;
        }
        return "-";
      },
    },
    {
      title: "Agent",
      dataIndex: "agent_name",
      key: "agent_name",
      width: 200,
      render: (name, record) => {
        if (name && record.agent_account_no) {
          return `${name} (${record.agent_account_no})`;
        } else if (name) {
          return name;
        } else if (record.agent_id) {
          return `Agent ID: ${record.agent_id}`;
        }
        return "-";
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (text) => text || "-",
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      width: 120,
      render: (value) => parseFloat(value || 0).toFixed(2),
      align: "right",
    },
    {
      title: "Total Discount",
      dataIndex: "total_discount",
      key: "total_discount",
      width: 120,
      render: (value) => parseFloat(value || 0).toFixed(2),
      align: "right",
    },
    {
      title: "Paid Amount",
      dataIndex: "paid_amount",
      key: "paid_amount",
      width: 120,
      render: (value) => parseFloat(value || 0).toFixed(2),
      align: "right",
    },
    {
      title: "Outstanding Amount",
      dataIndex: "outstanding",
      key: "outstanding",
      width: 150,
      render: (value) => (
        <span style={{ fontWeight: "bold", color: "#ff4d4f" }}>
          {parseFloat(value || 0).toFixed(2)}
        </span>
      ),
      align: "right",
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      key: "payment_status",
      width: 120,
      render: (status) => {
        if (!status) {
          return "-";
        }
        
        let color = "red";
        let displayStatus = status;
        
        if (displayStatus === "paid") {
          color = "green";
        } else if (displayStatus === "partial" || displayStatus === "partail") {
          color = "orange";
          displayStatus = "partial";
        } else {
          color = "red";
        }
        
        return (
          <Tag color={color} style={{ textTransform: "uppercase" }}>
            {displayStatus}
          </Tag>
        );
      },
    },
    {
      title: "Sale Date",
      dataIndex: "payment_date",
      key: "payment_date",
      width: 150,
      render: (date) => date ? moment(date).format("YYYY-MM-DD") : "-",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (date) => date ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "-",
    },
  ];

  const pagination = {
    current: parseInt(meta.page) || 1,
    pageSize: parseInt(meta.take) || 10,
    total: parseInt(meta.itemCount) || 0,
    showSizeChanger: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} items`,
    pageSizeOptions: ["10", "20", "50", "100"],
    onChange: (page, pageSize) => {
      onPageChange(page, pageSize);
    },
  };

  return (
    <div style={{ padding: "20px", background: "#fff" }}>
      {/* Totals Summary */}
      {totals && (
        <div
          style={{
            marginBottom: "20px",
            padding: "15px",
            background: "#f5f5f5",
            borderRadius: "4px",
            display: "flex",
            gap: "30px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <strong>Total Unpaid Sales:</strong>{" "}
            <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
              {totals.total_unpaid_sales || 0}
            </span>
          </div>
          <div>
            <strong>Total Partial Sales:</strong>{" "}
            <span style={{ color: "#fa8c16", fontWeight: "bold" }}>
              {totals.total_partial_sales || 0}
            </span>
          </div>
          <div>
            <strong>Total Sales:</strong>{" "}
            <span style={{ fontWeight: "bold" }}>
              {totals.total_sales || 0}
            </span>
          </div>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        scroll={{ x: 1200 }}
        size="small"
        expandedRowRender={(record) => {
          if (!record.sale_items || record.sale_items.length === 0) {
            return <div style={{ padding: "10px" }}>No items found</div>;
          }
          return (
            <div style={{ padding: "10px", background: "#fafafa" }}>
              <h4 style={{ marginBottom: "10px" }}>Sale Items:</h4>
              <Table
                columns={[
                  {
                    title: "Item Name",
                    dataIndex: "name",
                    key: "name",
                    width: 150,
                  },
                  {
                    title: "Description",
                    dataIndex: "description",
                    key: "description",
                    width: 200,
                  },
                  {
                    title: "Price",
                    dataIndex: "price",
                    key: "price",
                    width: 100,
                    render: (value) => parseFloat(value || 0).toFixed(2),
                    align: "right",
                  },
                  {
                    title: "Quantity",
                    dataIndex: "quantity",
                    key: "quantity",
                    width: 100,
                    render: (value) => parseFloat(value || 0).toFixed(2),
                    align: "right",
                  },
                  {
                    title: "Discount",
                    dataIndex: "discount",
                    key: "discount",
                    width: 100,
                    render: (value) => parseFloat(value || 0).toFixed(2),
                    align: "right",
                  },
                  {
                    title: "Paid Amount",
                    dataIndex: "paid_amount",
                    key: "paid_amount",
                    width: 120,
                    render: (value) => parseFloat(value || 0).toFixed(2),
                    align: "right",
                  },
                  {
                    title: "Payment Status",
                    dataIndex: "payment_status",
                    key: "payment_status",
                    width: 120,
                    render: (status) => {
                      if (!status) {
                        return "-";
                      }
                      
                      let color = "red";
                      let displayStatus = status;
                      
                      if (displayStatus === "paid") {
                        color = "green";
                      } else if (displayStatus === "partial") {
                        color = "orange";
                      } else {
                        color = "red";
                      }
                      
                      return (
                        <Tag color={color} style={{ textTransform: "uppercase" }}>
                          {displayStatus}
                        </Tag>
                      );
                    },
                  },
                  {
                    title: "Total",
                    dataIndex: "total",
                    key: "total",
                    width: 120,
                    render: (value) => (
                      <span style={{ fontWeight: "bold" }}>
                        {parseFloat(value || 0).toFixed(2)}
                      </span>
                    ),
                    align: "right",
                  },
                ]}
                dataSource={record.sale_items}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </div>
          );
        }}
      />
    </div>
  );
}

export default SalePendingDataTable;

